import { CancellationToken } from 'infra/cancellation'
import { PublishedLegend } from 'legends/PublishedLegend'
import truncate from 'lodash/truncate'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { combineLatest, Observable, of, Subject, firstValueFrom } from 'rxjs'
import { switchMap, takeUntil } from 'rxjs/operators'
import { fromStream, toStream } from 'utils/mobx-util'
import { filterNonNil, shareLatest, unpair } from 'utils/rx-util'
import Member from '../../iam/models/Member'
import {
  RequestedProviderChanged,
  RequestedProviderSelection,
} from './DraftChange'
import { DraftStateSlice, PrepareContext } from './DraftStateSlice'
import {
  OutlineItemDisplayVisual,
  StepContainer,
  StepFactory,
  StepViewModel,
} from './OutlineViewModel'
import { ProviderLookupPort } from './ports'

const TITLE = 'Would you like to reconnect with a previous Pro?'
const DESCRIPTION = `
Please keep in mind that if the Pro selected does not have
capacity to take on more jobs, your job may be transferred
to another qualified Pro. Choosing this option will manually
route you to the selected Pro. Please do not expect an
immediate response from them upon submission. The Pro will
be notified the job has been routed and they will reach out
to you next time they log in.
`
  .trim()
  .replace(/\n/g, ' ')

/**
 * Provider Selection VM.
 */
export default class PreviousProviderSelectionViewModel extends DraftStateSlice {
  /**
   * Changes subject.
   * @private
   */
  private readonly changesSubject = new Subject<RequestedProviderChanged>()

  /**
   * Changes as observable.
   */
  readonly changes$ = this.changesSubject.asObservable()

  /**
   * Step VM.
   */
  readonly step: StepViewModel

  /**
   * The selected provider, if any.
   * `null` = no selection
   * `Member.none` = first available
   */
  @observable selected: RequestedProviderSelection | null = null

  /**
   * Stream for whether the step is valid.
   */
  isValid$ = toStream(() => this.isValid, true)

  /**
   * Stream of available providers. Emits after first query.
   */
  availableProviders$: Observable<Member[]> = combineLatest([
    // We need the selected WS member and the selected Legend
    // to figure out whether we should query for pros and for which WS.
    this.member$,
    this.legend$,
  ]).pipe(
    unpair(),
    // Don't do anything until we have selected both WS and Legend.
    filterNonNil(),
    switchMap((member) =>
      toStream(() => this.isAllowedToSelectProvider, true).pipe(
        switchMap((allowed) =>
          allowed ? this.fetchPreviousProvidersForMember(member) : of([]),
        ),
      ),
    ),
    shareLatest(),
    this.whileActive(),
  )

  /**
   * Streams as observables.
   * @private
   */
  private readonly $ = {
    member: fromStream(Member.none, this.member$),
    legend: fromStream(PublishedLegend.none, this.legend$),
    availableProviders: fromStream([], this.availableProviders$),
  }

  /**
   * Constructor.
   */
  constructor(
    isActive$: Observable<boolean>,
    createStep: StepFactory,
    private adapter: ProviderLookupPort,
    private member$: Observable<Member | null>,
    private legend$: Observable<PublishedLegend | null>,
    private _isDisabled: () => boolean,
    private _canSaveJobAsAdmin: () => boolean,
  ) {
    super(isActive$)

    makeObservable(this)

    this.step = createStep({
      stepId: 'provider_selection',
      isValid: () => Boolean(this.selected),
      outlineItemDisplay: () => {
        if (!this.selected) {
          return {
            title: TITLE,
            description: DESCRIPTION,
          }
        }

        const value =
          this.selected === RequestedProviderSelection.firstAvailable
            ? 'First Available'
            : this.selected.displayName

        const visual: OutlineItemDisplayVisual =
          this.selected === RequestedProviderSelection.firstAvailable
            ? { type: 'EmptyAvatar' }
            : { type: 'Image', url: this.selected.avatar }

        return {
          value,
          visual,
          title: TITLE,
          description: DESCRIPTION,
          shortValue: truncate(value, { length: 25 }),
        }
      },
    })
  }

  /**
   * Whether the step is valid.
   */
  @computed
  get isValid() {
    return !this.isProviderSelectionRelevant || Boolean(this.selected)
  }

  /**
   * Whether the editor is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * Available providers.
   */
  @computed
  get availableProviders() {
    return this.$.availableProviders()
  }

  /**
   * Whether selecting a provider is allowed.
   */
  @computed
  get isAllowedToSelectProvider() {
    return Boolean(this.$.legend()?.allowProSelection)
  }

  /**
   * Whether provider selection is relevant.
   */
  @computed
  get isProviderSelectionRelevant() {
    return (
      !this._canSaveJobAsAdmin() &&
      this.isAllowedToSelectProvider &&
      this.availableProviders.length > 0
    )
  }

  /**
   * The step containers.
   */
  @computed
  get stepContainers(): StepContainer[] {
    return this.isProviderSelectionRelevant ? [this] : []
  }

  /**
   * Prepares the VM.
   *
   * @param context
   * @param ct
   */
  async prepare(context: PrepareContext, ct: CancellationToken): Promise<void> {
    if (context.type !== 'EditDraft') {
      return
    }

    await this.waitForProvidersToLoad(ct)
    ct.throwIfCancelled()

    if (!context.job.requestedProId) {
      return
    }

    const pro = this.availableProviders.find(
      (p) => p.user.id === context.job.requestedProId,
    )

    if (pro) {
      // The pro is still available, so restore that state.
      this.setRequestedPro(pro)
    } else {
      // The pro is no longer in the list, so emit a change that
      // resets to no selection.
      this.select(null)
    }
  }

  /**
   * Selects the requested provider and emits a change.
   *
   * @param requestedProvider
   */
  @action.bound
  select(requestedProvider: RequestedProviderSelection | null | undefined) {
    requestedProvider ??= null

    // Only emit the change if we actually made a change.
    if (this.selected !== requestedProvider) {
      this.setRequestedPro(requestedProvider)
      this.changesSubject.next({
        requestedProvider,
        type: 'RequestedProviderChanged',
      })
    }
  }

  /**
   * Sets the requested Pro.
   *
   * @param requestedProvider
   */
  @action.bound
  setRequestedPro(requestedProvider: RequestedProviderSelection | null) {
    this.selected = requestedProvider
  }

  /**
   * Waits for available providers to emit once.
   *
   * @param ct
   */
  waitForProvidersToLoad = async (ct: CancellationToken) => {
    await firstValueFrom(
      this.availableProviders$.pipe(takeUntil(ct.asObservable())),
      {
        defaultValue: undefined,
      },
    )
  }

  /**
   * Fetches previous providers for a member.
   */
  fetchPreviousProvidersForMember = task(
    this.adapter.fetchPreviousProvidersForMember,
  )
}
